import React, { useState, useRef, useEffect } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";
import Select, { components } from "react-select";
import { fixedWidthCustomStyle } from "components/Common/constants";
import CommonTable from "components/ReactTable/Table";
import { DEFAULT_RECORDS_PER_PAGE } from "components/Common/constants";
import NotificationAlert from "react-notification-alert";
import { dashboardPaymentFailedAndSuccessList } from "actions/dashboard";
import { useNavigate } from "react-router-dom";
import { exportPaymentList } from "actions/dashboard";
import moment from "moment";

// Hardcoded options for Payment Status
const paymentStatusOptions = [
  { value: "SUCCESS", label: "SUCCESS" },
  { value: "FAILED", label: "FAILED" },
  { value: "PENDING", label: "PENDING" },
  { value: "CANCELLED", label: "CANCELLED" },
];

// Hardcoded options for Days Filter
const daysOptions = [
  { value: 7, label: "Last 7 Days" },
  { value: 15, label: "Last 15 Days" },
  { value: 30, label: "Last 30 Days" },
];

function PaymentList() {
  const navigate = useNavigate();
  const [paymentResponse, setPaymentResponse] = useState(null);

  const [filterState, setFilterState] = useState({
    searchWorkOrderID: "",    // Work Order ID (sent via filters)
    searchTransactionID: "",  // ✅ New: Transaction ID (sent via filters)
    searchStatus: null,
    searchDays: null,
    filterParams: [],
  });

  const notificationAlertRef = useRef(null);
  const [pageNum, setPageNum] = useState(1);
  const [paymentListState, setPaymentListState] = useState({
    isLoading: true,
    showLoaderOnClick: false,
    error: "",
    noRecords: false,
  });

  // --- Build filter params from UI state ---
  const buildFilterParams = () => {
    const filterParams = [];

    // ✅ Work Order ID filter
    if (filterState.searchWorkOrderID.trim()) {
      filterParams.push({
        field: "WorkOrderID",
        condition: "eq",
        filterValues: [filterState.searchWorkOrderID.trim()],
      });
    }

    // ✅ New: Transaction ID filter (MerchantTransactionID)
    if (filterState.searchTransactionID.trim()) {
      filterParams.push({
        field: "MerchantTransactionID",
        condition: "eq",
        filterValues: [filterState.searchTransactionID.trim()],
      });
    }

    // Payment Status filter
    if (filterState.searchStatus) {
      filterParams.push({
        field: "PaymentStatus",
        condition: "eq",
        filterValues: [filterState.searchStatus.value],
      });
    }

    // Days filter
    if (filterState.searchDays) {
      filterParams.push({
        field: "InitiatedAt",
        condition: "interval",
        filterValues: [String(filterState.searchDays?.value)],
      });
    }

    return {
      filters:
        filterParams.length > 0 ? JSON.stringify(filterParams) : undefined,
    };
  };

  // --- Fetch Payment list ---
  useEffect(() => {
    const fetchData = async () => {
      setPaymentListState((prev) => ({ ...prev, isLoading: true }));

      const payload = {
        pageNum: pageNum,
      };

      const f = filterState.filterParams.filters;
      if (f && f !== "[]" && typeof f === "string" && f.length > 2) {
        payload.filters = f;
      }

      try {
        const res = await dashboardPaymentFailedAndSuccessList(payload);
        setPaymentResponse(res?.data || res);
      } catch (err) {
        setPaymentResponse({
          code: 500,
          message: "Failed to load the payment list! Please try again.",
          data: null,
        });
      }
    };

    fetchData();
  }, [pageNum, filterState.filterParams]);

  // --- Sync loader / error state from the response ---
  useEffect(() => {
    if (!paymentResponse) return;

    if (paymentResponse.code === 200) {
      setPaymentListState({
        isLoading: false,
        error: "",
        noRecords: (paymentResponse?.data?.payment?.length ?? 0) === 0,
        showLoaderOnClick: false,
      });
    } else if (paymentResponse.code || paymentResponse.message) {
      setPaymentListState({
        isLoading: false,
        error:
          paymentResponse.message ||
          "Failed to load the payment list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [paymentResponse]);

  // ✅ Formatted list
  const formattedPaymentList =
    paymentResponse?.data?.payment?.map((item, index) => ({
      ...item,
      srNo:
        (pageNum - 1) *
        (paymentResponse?.data?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
    })) ?? [];

  const totalRecords = paymentResponse?.data?.totalRecords || 0;
  const totalFilteredRecords = paymentResponse?.data?.totalFilteredRecords || 0;
  const noOfRecordsPerPage =
    paymentResponse?.data?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE;

  const handleSearch = () => {
    if (
      !filterState.searchWorkOrderID.trim() &&
      !filterState.searchTransactionID.trim() &&
      !filterState.searchStatus &&
      !filterState.searchDays
    ) {
      return;
    }

    const newfilterParams = buildFilterParams();
    if (
      JSON.stringify(filterState.filterParams) !==
      JSON.stringify(newfilterParams)
    ) {
      setPaymentListState((prev) => ({
        ...prev,
        showLoaderOnClick: true,
        isLoading: true,
      }));
      setFilterState((prev) => ({
        ...prev,
        filterParams: newfilterParams,
      }));
      if (pageNum !== 1) setPageNum(1);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleClear = () => {
    setPaymentListState((prev) => ({ ...prev, isLoading: true }));
    setFilterState({
      searchWorkOrderID: "",
      searchTransactionID: "",
      searchStatus: null,
      searchDays: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  // --- Table columns ---
  const columns = [
    { Header: "Sr. no.", accessor: "srNo", centerAlign: true, className: "col-srNo" },
    {
      Header: "Work Order No",
      className: "col-work-order",
      Cell: ({ row }) => <div>{row.original.jobID || "-"}</div>,
    },
    {
      Header: "Company",
      className: "col-company-outlet",
      Cell: ({ row }) => <div>{row.original.companyName || "-"}</div>,
    },
    {
      Header: "Outlet",
      className: "col-company-outlet",
      Cell: ({ row }) => <div>{row.original.outletName || "-"}</div>,
    },
    {
      Header: "Amount",
      accessor: "payableAmount",
      className: "col-amount",
      Cell: ({ row }) => (
        <span>
          {row.original.payableAmount} {row.original.currency}
        </span>
      ),
    },
    {
      Header: "Method",
      accessor: "paymentMethod",
      className: "col-method",
      Cell: ({ row }) => (
        <span>
          {row.original.paymentMethod} <br />
          <small className="text-muted">{row.original.paymentType}</small>
        </span>
      ),
    },
    {
      Header: "Status",
      accessor: "paymentStatus",
      className: "col-payment-status",
      Cell: ({ value }) => (
        <Badge color={getStatusBadgeColor(value)} pill>
          {value || "-"}
        </Badge>
      ),
    },
    {
      Header: "Initiated At",
      accessor: "initiatedAt",
      className: "col-date",
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
    {
      Header: "Transaction ID",
      accessor: "merchantTransactionID",
      className: "col-txn-id",
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
    {
      Header: "Result Code",
      accessor: "resultCode",
      className: "col-txn-id",
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
    {
      Header: "Result Message",
      accessor: "resultMessage",
      className: "col-txn-id",
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },

  ];

  // ✅ Helper function to get badge color based on status
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "SUCCESS":
        return "success";
      case "FAILED":
        return "danger";
      case "PENDING":
        return "warning";
      case "CANCELLED":
        return "secondary";
      default:
        return "light";
    }
  };

  const handleExportCsv = async () => {
    try {
      // setIsSaveClicked((prev) => ({ ...prev, exportCsv: true }));
      const res = await exportPaymentList(filterState.filterParams);
      if (res.status === 200) {
        const blob = new Blob(["\uFEFF" + res?.data], {
          type: "text/csv;charset=utf-8;",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `parts-requested-report_${moment().format("YYYY-MM-DD")}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        // notify("success", res?.message || "CSV exported successfully");
      } else {
        const message = Array.isArray(res?.message || res?.data?.message)
          ? res?.message[0]?.Msg || res?.data?.message[0]?.Msg
          : res?.message || res?.data?.message || "Failed to export csv, try again later";
        // notify("danger", message);
      }
    } catch (error) {
      // notify("danger", error?.message || "Something went wrong");
    } finally {
      // setIsSaveClicked((prev) => ({ ...prev, exportCsv: false }));
    }
  };


  const Option = (props) => (
    <components.Option {...props}>
      {["SUCCESS", "FAILED", "PENDING", "CANCELLED"].includes(
        props.data.value
      ) ? (
        <Badge color={getStatusBadgeColor(props.data.value)} pill>
          {props.data.label}
        </Badge>
      ) : (
        props.data.label
      )}
    </components.Option>
  );

  const SingleValue = (props) => (
    <components.SingleValue {...props}>
      {["SUCCESS", "FAILED", "PENDING", "CANCELLED"].includes(
        props.data.value
      ) ? (
        <Badge color={getStatusBadgeColor(props.data.value)} pill>
          {props.data.label}
        </Badge>
      ) : (
        props.data.label
      )}
    </components.SingleValue>
  );

  return (
    <>
      <div className="rna-wrapper">
        <NotificationAlert ref={notificationAlertRef} />
      </div>

      <Container fluid className="mt-4">
        <Row>
          <Col className="mb-5" md="12">
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between mb-2">

                <CardTitle tag="h3" className="font-weight-normal mb-0">
                  Payment List
                </CardTitle>
                <div className="d-flex align-items-center">
                  <Button
                    className="btn-simple btn-round btn-style mr-3"
                    color="default"
                    size="md"
                    onClick={()=>handleExportCsv()}
                  >
                    <i className="fas fa-arrow-up-from-bracket mr-1" /> Export Payment List
                  </Button>
                </div>

              </CardHeader>

              <CardBody>
                {/* Filters */}
                <div className="d-flex align-items-center justify-content-between flex-wrap flex-column flex-md-row">
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    {/* Work Order ID → goes into filters */}
                    <Input
                      type="text"
                      placeholder="Search by Work Order No"
                      className="mr-2 mb-2 filter-input-small"
                      value={filterState.searchWorkOrderID}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchWorkOrderID: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                      maxLength={150}
                    />

                    {/* Transaction ID → goes into filters (MerchantTransactionID) */}
                    <Input
                      type="text"
                      placeholder="Search by Transaction ID"
                      className="mr-2 mb-2 filter-input-small"
                      value={filterState.searchTransactionID}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchTransactionID: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                      maxLength={150}
                    />

                    {/* Days Filter */}
                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={daysOptions}
                      placeholder="Select Days"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      value={filterState.searchDays}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchDays: selected,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    {/* Payment Status Filter */}
                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={paymentStatusOptions}
                      placeholder="Payment Status"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      components={{ Option, SingleValue }}
                      value={filterState.searchStatus}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchStatus: selected,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Button
                      color="default"
                      className="d-flex align-items-center justify-content-center mb-3 filter-btn btn-simple mt-2 mr-1"
                      onClick={handleSearch}
                    >
                      {paymentListState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin search-loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {(filterState.filterParams?.filters?.length > 0 ||
                      filterState.searchWorkOrderID.trim() ||
                      filterState.searchTransactionID.trim()) && (
                        <Button
                          color="default"
                          className="d-flex align-items-center justify-content-center mt-2 mb-3 filter-btn btn-simple"
                          onClick={handleClear}
                        >
                          <i className="fa fa-times mr-1" />
                          Clear
                        </Button>
                      )}
                  </div>
                </div>

                {/* Record count */}
                <div className="mt-2 mb-3 mx-1">
                  {totalRecords > 0 && (
                    <>
                      {filterState.filterParams?.filters?.length > 0 ||
                        filterState.searchWorkOrderID.trim() ||
                        filterState.searchTransactionID.trim() ? (
                        <span className="font-weight-bold">
                          Showing {totalFilteredRecords} of {totalRecords} Payments
                        </span>
                      ) : (
                        <span className="font-weight-bold">
                          Total Payments : {totalRecords}
                        </span>
                      )}
                    </>
                  )}
                </div>

                {/* Table */}
                <div className="table-responsive">
                  {paymentListState.isLoading ? (
                    <div className="loaderContainer">
                      <i className="fa fa-spinner fa-spin fa-2xl loader" />
                    </div>
                  ) : paymentListState.error ? (
                    <div className="loaderContainer text-muted d-flex flex-column">
                      <h4 className="text-muted">Failed to load payment list</h4>
                      <span className="validation-error">
                        {paymentListState.error}
                      </span>
                    </div>
                  ) : paymentListState.noRecords ? (
                    <div className="loaderContainer text-muted">
                      No payments found.
                    </div>
                  ) : (
                    <CommonTable
                      data={formattedPaymentList}
                      resizable={false}
                      columns={columns}
                      defaultPageSize={noOfRecordsPerPage}
                      activePage={pageNum}
                      showPaginationBottom={true}
                      className="-striped -highlight table-hover"
                      totalPages={Math.ceil(
                        totalFilteredRecords / noOfRecordsPerPage
                      )}
                      setPageNum={setPageNum}
                    />
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default PaymentList;